// Hasta Dağılımı sekmesi — kliniğin aktif hastalarının demografisi.
// Dashboard dili: slate50 zemin + bölüm kartları (SectionCard).
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  alpha,
} from "@mui/material";
import {
  Wc as GenderIcon,
  TrendingUp as TrendIcon,
  Bloodtype as BloodIcon,
  LocalHospital as DiagnosisIcon,
  Place as PlaceIcon,
  InsertChartOutlined as ChartIcon,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";
import useAuth from "../../../hooks/useAuth";
import useTerms from "../../../hooks/useTerms";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { usePatientDistributionMutation } from "../../../store";
import KpiTile from "../../../components/molecules/KpiTile";
import EmptyState from "../../../components/atoms/EmptyState";
import {
  AXIS_TICK,
  GRID,
  ChartTip,
  RankBarList,
  BodySkeleton,
  CardEmpty,
  AnalyticsCard,
  nf,
  pct,
} from "../../../components/organisms/charts/analyticsParts";
import { GENDER_COLORS } from "../../../components/organisms/charts/demographicsTheme";
import { COLORS, TYPO, CARD_GAP, CARD_PAGE_PAD } from "../../../styles/uiConstants";

const PLACEHOLDER = {
  total: 0,
  averageAge: null,
  unknownAge: 0,
  activeTreatmentCount: 0,
  gender: { Kadın: 0, Erkek: 0, Belirtilmemiş: 0 },
  agePyramid: [],
  bloodTypes: [],
  monthlyNew: [],
  topDiagnoses: [],
  districts: [],
};

/**
 * Yalnız geliştirmede (npm start) durum önizlemesi: URL'ye `&onizleme=` ekle.
 *   bos        → hasta var ama hiçbir kartta veri yok (kart içi boş durumlar)
 *   hic        → hiç hasta yok (sayfa geneli boş durum)
 *   yukleniyor → yükleme iskeleti
 * Production build'de yok sayılır.
 */
const PREVIEWS = {
  bos: {
    ...PLACEHOLDER,
    total: 12,
    unknownAge: 12,
    gender: { Kadın: 0, Erkek: 0, Belirtilmemiş: 12 },
    agePyramid: [
      "0-17",
      "18-29",
      "30-39",
      "40-49",
      "50-59",
      "60-69",
      "70+",
    ].map((group) => ({ group, Kadın: 0, Erkek: 0, Belirtilmemiş: 0 })),
    bloodTypes: ["A+", "A-", "B+", "B-", "AB+", "AB-", "0+", "0-"]
      .map((type) => ({ type, count: 0 }))
      .concat({ type: "Bilinmiyor", count: 12 }),
    monthlyNew: [],
  },
  hic: PLACEHOLDER,
};

const TR_MONTHS = [
  "Oca",
  "Şub",
  "Mar",
  "Nis",
  "May",
  "Haz",
  "Tem",
  "Ağu",
  "Eyl",
  "Eki",
  "Kas",
  "Ara",
];
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return `${TR_MONTHS[m - 1]}${m === 1 ? ` ${String(y).slice(2)}` : ""}`;
};

function GenderLegend({ gender, total }) {
  return (
    <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mb: 1.5 }}>
      {["Kadın", "Erkek"].map((g) => (
        <Box key={g} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: 0.75,
              bgcolor: GENDER_COLORS[g],
            }}
          />
          <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }}>
            {g} <strong>{nf(gender[g])}</strong>
            <Box component="span" sx={{ color: COLORS.slate500 }}>
              {" "}
              · %{pct(gender[g], total)}
            </Box>
          </Typography>
        </Box>
      ))}
      {gender.Belirtilmemiş ? (
        <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate500 }}>
          Belirtilmemiş {nf(gender.Belirtilmemiş)}
        </Typography>
      ) : null}
    </Box>
  );
}

const card = (title, subtitle, icon, children, bodyHeight) => (
  <AnalyticsCard title={title} subtitle={subtitle} icon={icon} bodyHeight={bodyHeight}>
    {children}
  </AnalyticsCard>
);

export default function PatientDistributionTab() {
  const terms = useTerms();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showError } = useAlert();
  const [load, { isLoading }] = usePatientDistributionMutation();
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) return;
    load({ token })
      .unwrap()
      .then((res) => setResult(res?.data || null))
      .catch((e) => showError(e?.data?.message || `${terms.patient} dağılımı alınamadı.`));
  }, [token, load, showError, terms]);

  const wrap = (children) => (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        bgcolor: COLORS.slate50,
        ...CARD_PAGE_PAD,
      }}
    >
      {children}
    </Box>
  );

  const [searchParams] = useSearchParams();
  const preview =
    process.env.NODE_ENV === "development"
      ? searchParams.get("onizleme")
      : null;

  const loading =
    preview === "yukleniyor" || (!PREVIEWS[preview] && (isLoading || !result));
  const data = PREVIEWS[preview] || result || PLACEHOLDER;

  if (!loading && !data.total) {
    return wrap(
      <EmptyState
        variant="illustrated"
        icon={<ChartIcon />}
        title="Henüz dağılım yok"
        description={`Kliniğe ${terms.patientLower} eklendikçe yaş, cinsiyet ve kan grubu dağılımları burada görünecek.`}
        sx={{ mt: 6 }}
      />,
    );
  }

  const total = data.total;
  const pyramid = data.agePyramid.map((g) => ({
    group: g.group,
    Kadın: -g.Kadın,
    Erkek: g.Erkek,
  }));
  // Simetrik, tam sayılı eksen: -max … 0 … max (yarı adımlarla)
  const rawMax = Math.max(
    1,
    ...data.agePyramid.map((g) => Math.max(g.Kadın, g.Erkek)),
  );
  const half = Math.ceil(rawMax / 2);
  const pyramidMax = half * 2;
  const pyramidTicks = [-pyramidMax, -half, 0, half, pyramidMax];
  const knownBlood = data.bloodTypes.filter((b) => b.type !== "Bilinmiyor");
  const bloodKnownTotal = knownBlood.reduce((sum, b) => sum + b.count, 0);
  const pyramidEmpty = data.agePyramid.every((g) => !g.Kadın && !g.Erkek);
  const unknownBlood =
    data.bloodTypes.find((b) => b.type === "Bilinmiyor")?.count || 0;
  const monthly = data.monthlyNew.map((m) => ({
    ...m,
    label: monthLabel(m.month),
  }));
  const last12 = monthly.reduce((s, m) => s + m.count, 0);
  const primary = palette.primary;

  return wrap(
    <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
      {/* Özet */}
      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" },
        }}
      >
        <KpiTile
          loading={loading}
          label={`Aktif ${terms.patientLower}`}
          value={nf(total)}
          hint={`Son 12 ayda ${nf(last12)} yeni katılım`}
        />
        <KpiTile
          loading={loading}
          label="Ortalama yaş"
          value={data.averageAge != null ? nf(data.averageAge) : "—"}
          hint={
            data.unknownAge
              ? `${nf(data.unknownAge)} ${terms.patientGenLower} doğum tarihi yok`
              : `Tüm ${terms.patientsLower} dahil`
          }
        />
        <KpiTile
          loading={loading}
          label="Kadın / Erkek"
          value={
            data.gender.Kadın + data.gender.Erkek
              ? `%${pct(data.gender.Kadın, total)} / %${pct(data.gender.Erkek, total)}`
              : "—"
          }
          hint={
            data.gender.Kadın + data.gender.Erkek
              ? `${nf(data.gender.Kadın)} kadın · ${nf(data.gender.Erkek)} erkek`
              : "Cinsiyet bilgisi girilmemiş"
          }
        />
        <KpiTile
          loading={loading}
          label="Aktif tedavide"
          value={`%${pct(data.activeTreatmentCount, total)}`}
          hint={`${nf(data.activeTreatmentCount)} ${terms.patientLower} tedavi sürecinde`}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" },
        }}
      >
        {card(
          "Yaş ve cinsiyet",
          `Yaş gruplarına göre kadın ve erkek ${terms.patientLower} sayısı`,
          <GenderIcon />,
          loading ? (
            <BodySkeleton kind="pyramid" />
          ) : pyramidEmpty ? (
            <CardEmpty
              title="Yaş bilgisi yok"
              description={`${terms.patientsGen} doğum tarihi girildikçe yaş dağılımı burada görünür.`}
            />
          ) : (
            <>
              <GenderLegend gender={data.gender} total={total} />
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={pyramid}
                    layout="vertical"
                    stackOffset="sign"
                    barSize={18}
                    margin={{ top: 4, right: 16, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid
                      stroke={COLORS.slate100}
                      horizontal={false}
                    />
                    <XAxis
                      type="number"
                      domain={[-pyramidMax, pyramidMax]}
                      ticks={pyramidTicks}
                      allowDecimals={false}
                      tickFormatter={(v) => nf(Math.abs(v))}
                      tick={AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="group"
                      tick={AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                      width={48}
                    />
                    <ReferenceLine x={0} stroke={COLORS.slate300} />
                    <Tooltip
                      cursor={{ fill: alpha(COLORS.slate200, 0.4) }}
                      content={
                        <ChartTip
                          format={(p) => `${nf(Math.abs(p.value))} ${terms.patientLower}`}
                        />
                      }
                    />
                    <Bar
                      dataKey="Kadın"
                      name="Kadın"
                      stackId="g"
                      fill={GENDER_COLORS.Kadın}
                      radius={[4, 0, 0, 4]}
                    />
                    <Bar
                      dataKey="Erkek"
                      name="Erkek"
                      stackId="g"
                      fill={GENDER_COLORS.Erkek}
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </>
          ),
          360,
        )}

        {card(
          `Aylık yeni ${terms.patientLower}`,
          `Son 12 ayda kliniğe katılan ${terms.patientLower} sayısı`,
          <TrendIcon />,
          loading ? (
            <BodySkeleton kind="months" />
          ) : !last12 ? (
            <CardEmpty
              title={`Son 12 ayda yeni ${terms.patientLower} yok`}
              description={`Kliniğe katılan ${terms.patientsLower} aylara göre burada görünür.`}
            />
          ) : (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={monthly}
                  barSize={18}
                  margin={{ top: 8, right: 4, left: -16, bottom: 0 }}
                >
                  <CartesianGrid {...GRID} />
                  <XAxis
                    dataKey="label"
                    tick={AXIS_TICK}
                    axisLine={{ stroke: COLORS.slate200 }}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: alpha(COLORS.slate200, 0.4) }}
                    content={
                      <ChartTip format={(p) => `${nf(p.value)} yeni ${terms.patientLower}`} />
                    }
                  />
                  <Bar
                    dataKey="count"
                    name={`Yeni ${terms.patientLower}`}
                    fill={primary}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ),
          360,
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr" },
        }}
      >
        {card(
          "Kan grubu",
          unknownBlood
            ? `${nf(unknownBlood)} ${terms.patientLocLower} kan grubu girilmemiş`
            : "Kayıtlı kan grubu dağılımı",
          <BloodIcon />,
          loading ? (
            <BodySkeleton kind="bars" />
          ) : !bloodKnownTotal ? (
            <CardEmpty
              title="Kan grubu girilmemiş"
              description={`${terms.patient} kartlarında kan grubu doldurulunca dağılım burada görünür.`}
            />
          ) : (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={knownBlood}
                  barSize={18}
                  margin={{ top: 8, right: 4, left: -20, bottom: 0 }}
                >
                  <CartesianGrid {...GRID} />
                  <XAxis
                    dataKey="type"
                    tick={AXIS_TICK}
                    axisLine={{ stroke: COLORS.slate200 }}
                    tickLine={false}
                    interval={0}
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    cursor={{ fill: alpha(COLORS.slate200, 0.4) }}
                    content={
                      <ChartTip
                        format={(p) =>
                          `${nf(p.value)} ${terms.patientLower} · %${pct(p.value, total)}`
                        }
                      />
                    }
                  />
                  <Bar
                    dataKey="count"
                    name={terms.patient}
                    fill={primary}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ),
          320,
        )}

        {card(
          "En sık tanılar",
          `Aktif tedavilerde, ${terms.patientLower} sayısına göre`,
          <DiagnosisIcon />,
          loading ? (
            <BodySkeleton kind="list" />
          ) : (
            <RankBarList
              items={data.topDiagnoses}
              color={primary}
              total={total}
              emptyText={{
                title: "Aktif tedavide tanı yok",
                description:
                  "Tanılı tedaviler başladıkça en sık tanılar burada sıralanır.",
              }}
            />
          ),
          320,
        )}

        {card(
          "İlçe",
          `Adres bilgisine göre ${terms.patientLower} dağılımı`,
          <PlaceIcon />,
          loading ? (
            <BodySkeleton kind="list" />
          ) : (
            <RankBarList
              items={data.districts.map((d) => ({
                ...d,
                muted: d.name === "Diğer",
              }))}
              color={primary}
              total={total}
              emptyText={{
                title: "Adres bilgisi yok",
                description:
                  `${terms.patient} adreslerinde ilçe girildikçe dağılım burada görünür.`,
              }}
            />
          ),
          320,
        )}
      </Box>
    </Box>,
  );
}
